import { useState, useCallback } from "react";

/**
 * useSubstepForm - Custom hook for managing substep form state and handlers
 * 
 * Consolidates all form-related state management including:
 * - Form data (basic fields)
 * - Modal states
 * 
 * @param {Object} config
 * @param {string} config.mode - 'add' or 'edit'
 * @param {Object} config.initialData - Initial form data (for edit mode)
 * @param {function} config.onUnsavedChanges - Callback when data changes
 * 
 * @returns {Object} Form state and handlers
 */
const useSubstepForm = ({ mode, initialData = {}, onUnsavedChanges }) => {
  // Form data
  const [shortDescription, setShortDescription] = useState(initialData.shortDescription || "");

  // Modal states
  const [isModalOpen, setModalOpen] = useState(false);
  const [comment, setComment] = useState("");

  /**
   * Handle short description change
   */
  const handleShortDescriptionChange = useCallback((value) => {
    // Handle both event objects (from TextArea) and direct values (from ShortDescriptionEditor)
    const newValue = typeof value === 'object' && value.target ? value.target.value : value;
    setShortDescription(newValue);
    // NOTE: onUnsavedChanges is intentionally NOT called here.
    // Dirty detection for short description is handled via the Lexical
    // undo history (CAN_UNDO_COMMAND) bubbled up as isShortDescriptionDirty
    // in AddEditSubstep to avoid false positives on editor initialisation.
  }, []);

  /**
   * Reset form to initial state
   */
  const resetForm = useCallback(() => {
    setShortDescription("");
    setComment("");
  }, []);

  return {
    // Form data
    shortDescription,
    setShortDescription,
    
    // Modal states
    isModalOpen,
    setModalOpen,
    comment,
    setComment,
    
    // Handlers
    handleShortDescriptionChange,
    resetForm,
  };
};

export default useSubstepForm;
